import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNotifications } from './NotificationContext';
import { useAuth } from './AuthContext';
import { userActivityApi } from '../services/api';
import { debugLog } from '../utils/debug';

const ActivityTrackerContext = createContext(null);

// ── Machine / Session identity ──────────────────────────────────────────────
// crypto.randomUUID() requires a secure context (HTTPS or localhost).
// Fall back to Math.random()-based UUID for plain HTTP on the local network.
const genUUID = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
  });
};

const getMachineId = () => {
  let id = localStorage.getItem('dawn.machineId');
  if (!id) { id = genUUID(); localStorage.setItem('dawn.machineId', id); }
  return id;
};
const getSessionId = () => {
  let id = sessionStorage.getItem('dawn.sessionId');
  if (!id) { id = genUUID(); sessionStorage.setItem('dawn.sessionId', id); }
  return id;
};
const MACHINE_ID = getMachineId();
const SESSION_ID = getSessionId();

// ── Helpers ──────────────────────────────────────────────────────────────────
const getConcurrencyLimit = () => {
  const v = localStorage.getItem('activityTracker.concurrencyLimit');
  if (!v || v === 'unlimited') return Infinity;
  return parseInt(v, 10) || 2;
};

const activeLocalDownloadCount = (acts) =>
  acts.filter(a => a.source === 'local' && a.type === 'download' && a.status === 'active').length;

const classifySource = (a) => {
  if (a.sessionId === SESSION_ID) return 'local';
  if (a.machineId === MACHINE_ID) return 'other-tab';
  return 'remote';
};

// ── Provider ─────────────────────────────────────────────────────────────────
export const ActivityTrackerProvider = ({ children }) => {
  const { notify } = useNotifications();
  const { currentUser } = useAuth();
  const [activities, setActivities] = useState([]);
  const [clearMode, setClearModeState] = useState(
    () => localStorage.getItem('activityTracker.clearMode') || 'auto-1m'
  );

  const broadcastRef = useRef(null);
  const pollRef      = useRef(null);
  const clearTimers  = useRef({});
  // Keep a ref mirror of activities so callbacks (broadcast handler) can read current state
  const activitiesRef = useRef([]);
  useEffect(() => { activitiesRef.current = activities; }, [activities]);

  // ── BroadcastChannel: sync with other tabs on same machine ─────────────────
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const bc = new BroadcastChannel('dawn-activity');
    broadcastRef.current = bc;
    bc.onmessage = ({ data: evt }) => {
      debugLog('[ActivityTracker] BroadcastChannel recv:', evt);
      const { type, activityId, payload } = evt;
      if (type === 'register') {
        setActivities(prev => {
          const filtered = prev.filter(a => !(a.id === activityId && a.source !== 'local'));
          return [...filtered, { ...payload, source: 'other-tab', restartFn: undefined }];
        });
      } else if (type === 'update') {
        setActivities(prev => prev.map(a =>
          a.id === activityId && a.source === 'other-tab'
            ? { ...a, ...payload }
            : a
        ));
      } else if (type === 'complete') {
        setActivities(prev => prev.map(a =>
          a.id === activityId && a.source === 'other-tab'
            ? { ...a, status: 'complete', pct: 100 }
            : a
        ));
        // Also fire footer notification for completed downloads from this machine
        const act = activitiesRef.current.find(a => a.id === activityId);
        if (act?.type === 'download') {
          notify.success(`${act.name} — download complete (this machine)`, 5000);
        }
      } else if (type === 'fail') {
        setActivities(prev => prev.map(a =>
          a.id === activityId && a.source === 'other-tab'
            ? { ...a, status: 'failed' }
            : a
        ));
      }
    };
    return () => bc.close();
  }, [notify]);

  const broadcast = useCallback((type, activityId, payload) => {
    debugLog('[ActivityTracker] broadcast:', type, activityId);
    broadcastRef.current?.postMessage({ type, activityId, payload });
  }, []);

  // ── Backend polling: sync with other machines ──────────────────────────────
  useEffect(() => {
    if (!currentUser?.id) return;
    const userId = String(currentUser.id);

    const poll = async () => {
      try {
        const res = await userActivityApi.getActive(userId);
        const remote = (res.data || []).filter(a => a.sessionId !== SESSION_ID);
        debugLog('[ActivityTracker] poll result:', remote.length, 'remote entries');
        setActivities(prev => {
          const local = prev.filter(a => a.source === 'local');
          const classified = remote.map(a => ({ ...a, source: classifySource(a) }));
          return [...local, ...classified];
        });
      } catch (err) {
        debugLog('[ActivityTracker] poll error:', err.message);
      }
    };

    poll();
    pollRef.current = setInterval(poll, 5000);
    return () => clearInterval(pollRef.current);
  }, [currentUser?.id]);

  // ── Auto-clear scheduling ──────────────────────────────────────────────────
  const scheduleClear = useCallback((id) => {
    const mode = localStorage.getItem('activityTracker.clearMode') || 'auto-1m';
    const DELAYS = { 'auto-1m': 60000, 'auto-1d': 86400000, 'auto-1w': 604800000, 'auto-1mo': 2592000000 };
    if (!(mode in DELAYS)) return;
    const delay = DELAYS[mode];
    clearTimers.current[id] = setTimeout(() => {
      setActivities(prev => prev.filter(a => a.id !== id));
      delete clearTimers.current[id];
    }, delay);
  }, []);

  // ── startNextQueued: called after a slot opens ────────────────────────────
  const startNextQueued = useCallback((acts) => {
    const limit = getConcurrencyLimit();
    const count = activeLocalDownloadCount(acts);
    if (count >= limit) return;
    const next = [...acts]
      .filter(a => a.source === 'local' && a.status === 'queued')
      .sort((a, b) => a.queueOrder - b.queueOrder)[0];
    if (next?.restartFn) {
      debugLog('[ActivityTracker] auto-starting queued:', next.id);
      setActivities(prev => prev.map(a => a.id === next.id ? { ...a, status: 'active' } : a));
      next.restartFn();
    }
  }, []);

  // ── API: registerActivity ──────────────────────────────────────────────────
  const registerActivity = useCallback((id, { name, type, format, phase = '', pct = 0, restartFn, exportRecipe }) => {
    const limit = getConcurrencyLimit();

    setActivities(prev => {
      const filtered = prev.filter(a => a.id !== id);
      const activeCount = activeLocalDownloadCount(filtered);
      const status = (type === 'download' && activeCount >= limit) ? 'queued' : 'active';

      const entry = {
        id,
        name,
        type,
        format,
        phase,
        pct,
        status,
        source: 'local',
        machineId: MACHINE_ID,
        sessionId: SESSION_ID,
        machineName: window.location.hostname,
        userName: currentUser?.user_Name || currentUser?.displayName || null,
        restartFn,
        exportRecipe,
        queueOrder: Date.now(),
      };
      debugLog('[ActivityTracker] register:', id, status);
      return [...filtered, entry];
    });

    // Backend persist
    const userId = currentUser?.id ? String(currentUser.id) : null;
    const userName = currentUser?.user_Name || currentUser?.displayName || null;
    if (userId) {
      userActivityApi.register({
        userId,
        activityId: id,
        name,
        type,
        format,
        phase,
        pct,
        status: 'active',
        machineId: MACHINE_ID,
        sessionId: SESSION_ID,
        machineName: window.location.hostname,
        userName,
        exportRecipe: exportRecipe ? JSON.stringify(exportRecipe) : null,
      }).catch(() => {});
    }

    broadcast('register', id, { id, name, type, format, phase, pct, status: 'active', machineId: MACHINE_ID, sessionId: SESSION_ID, machineName: window.location.hostname, userName });
  }, [currentUser?.id, currentUser?.displayName, currentUser?.user_Name, broadcast]);

  // ── API: updateActivity ────────────────────────────────────────────────────
  const updateActivity = useCallback((id, { phase, pct }) => {
    setActivities(prev => prev.map(a =>
      a.id === id
        ? { ...a, ...(phase !== undefined ? { phase } : {}), ...(pct !== undefined ? { pct } : {}) }
        : a
    ));
    const userId = currentUser?.id ? String(currentUser.id) : null;
    if (userId) {
      userActivityApi.update(id, userId, {
        ...(phase !== undefined ? { phase } : {}),
        ...(pct   !== undefined ? { pct }   : {}),
      }).catch(() => {});
    }
    broadcast('update', id, { ...(phase !== undefined ? { phase } : {}), ...(pct !== undefined ? { pct } : {}) });
  }, [currentUser?.id, broadcast]);

  // ── API: completeActivity ──────────────────────────────────────────────────
  const completeActivity = useCallback((id) => {
    setActivities(prev => {
      const act = prev.find(a => a.id === id);
      if (!act) return prev;
      if (act.type === 'download') {
        notify.success(`${act.name} — download complete`, 5000);
      }
      const updated = prev.map(a => a.id === id ? { ...a, status: 'complete', pct: 100, phase: 'Complete' } : a);
      scheduleClear(id);
      setTimeout(() => startNextQueued(updated), 100);
      return updated;
    });

    const userId = currentUser?.id ? String(currentUser.id) : null;
    if (userId) {
      userActivityApi.update(id, userId, { status: 'complete', pct: 100 }).catch(() => {});
    }
    broadcast('complete', id, {});
  }, [currentUser?.id, notify, broadcast, scheduleClear, startNextQueued]);

  // ── API: failActivity ──────────────────────────────────────────────────────
  const failActivity = useCallback((id) => {
    setActivities(prev => {
      const act = prev.find(a => a.id === id);
      if (!act) return prev;
      if (act.type === 'download') {
        notify.warning(`${act.name} — download failed`, 5000);
      }
      const updated = prev.map(a => a.id === id ? { ...a, status: 'failed', phase: 'Failed' } : a);
      setTimeout(() => {
        setActivities(p => p.filter(a => a.id !== id));
        startNextQueued(updated);
      }, 5000);
      return updated;
    });

    const userId = currentUser?.id ? String(currentUser.id) : null;
    if (userId) {
      userActivityApi.update(id, userId, { status: 'failed' }).catch(() => {});
    }
    broadcast('fail', id, {});
  }, [currentUser?.id, notify, broadcast, startNextQueued]);

  // ── API: pauseActivity ────────────────────────────────────────────────────
  const pauseActivity = useCallback((id) => {
    setActivities(prev => {
      const updated = prev.map(a =>
        a.id === id && a.source === 'local' ? { ...a, status: 'paused' } : a
      );
      setTimeout(() => startNextQueued(updated), 100);
      return updated;
    });
    const userId = currentUser?.id ? String(currentUser.id) : null;
    if (userId) {
      userActivityApi.update(id, userId, { status: 'paused' }).catch(() => {});
    }
    broadcast('update', id, { status: 'paused' });
  }, [currentUser?.id, broadcast, startNextQueued]);

  // ── API: restartActivity ──────────────────────────────────────────────────
  const restartActivity = useCallback((id) => {
    const act = activitiesRef.current.find(a => a.id === id);
    if (!act?.restartFn) return;
    // Remove entry; registerActivity will re-add it
    setActivities(prev => prev.filter(a => a.id !== id));
    act.restartFn();
  }, []);

  // ── API: reorderQueue ─────────────────────────────────────────────────────
  const reorderQueue = useCallback((id, direction) => {
    setActivities(prev => {
      const queued = [...prev.filter(a => a.status === 'queued')].sort((a, b) => a.queueOrder - b.queueOrder);
      const idx = queued.findIndex(a => a.id === id);
      if (idx === -1) return prev;
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= queued.length) return prev;
      const [aOrder, bOrder] = [queued[idx].queueOrder, queued[swapIdx].queueOrder];
      return prev.map(a => {
        if (a.id === queued[idx].id)    return { ...a, queueOrder: bOrder };
        if (a.id === queued[swapIdx].id) return { ...a, queueOrder: aOrder };
        return a;
      });
    });
  }, []);

  // ── API: clearActivity (individual) ──────────────────────────────────────
  const clearActivity = useCallback((id) => {
    if (clearTimers.current[id]) {
      clearTimeout(clearTimers.current[id]);
      delete clearTimers.current[id];
    }
    setActivities(prev => prev.filter(a => a.id !== id));
    const userId = currentUser?.id ? String(currentUser.id) : null;
    if (userId) {
      userActivityApi.update(id, userId, { clearedAt: new Date().toISOString() }).catch(() => {});
    }
  }, [currentUser?.id]);

  // ── API: clearAllCompleted ────────────────────────────────────────────────
  const clearAllCompleted = useCallback(() => {
    const userId = currentUser?.id ? String(currentUser.id) : null;
    setActivities(prev => {
      const toRemove = prev.filter(a => a.status === 'complete' || a.status === 'failed');
      toRemove.forEach(a => {
        if (clearTimers.current[a.id]) {
          clearTimeout(clearTimers.current[a.id]);
          delete clearTimers.current[a.id];
        }
        if (userId) {
          userActivityApi.update(a.id, userId, { clearedAt: new Date().toISOString() }).catch(() => {});
        }
      });
      return prev.filter(a => a.status !== 'complete' && a.status !== 'failed');
    });
  }, [currentUser?.id]);

  // ── API: setClearMode ─────────────────────────────────────────────────────
  const setClearMode = useCallback((mode) => {
    localStorage.setItem('activityTracker.clearMode', mode);
    setClearModeState(mode);
  }, []);

  // ── Context value ─────────────────────────────────────────────────────────
  const value = useMemo(() => ({
    activities,
    registerActivity,
    updateActivity,
    completeActivity,
    failActivity,
    pauseActivity,
    restartActivity,
    reorderQueue,
    clearActivity,
    clearAllCompleted,
    clearMode,
    setClearMode,
    machineId: MACHINE_ID,
    sessionId: SESSION_ID,
  }), [
    activities,
    registerActivity, updateActivity, completeActivity, failActivity,
    pauseActivity, restartActivity, reorderQueue,
    clearActivity, clearAllCompleted, clearMode, setClearMode,
  ]);

  return (
    <ActivityTrackerContext.Provider value={value}>
      {children}
    </ActivityTrackerContext.Provider>
  );
};

export const useActivityTracker = () => {
  const ctx = useContext(ActivityTrackerContext);
  if (!ctx) throw new Error('useActivityTracker must be used inside ActivityTrackerProvider');
  return ctx;
};
